import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';

export type GaugeData = {
  promoters: number;
  passives: number;
  detractors: number;
  totalScore?: number;
};

type UseExportOptionsProps = {
  data: GaugeData;
  comparisonData?: GaugeData;
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const useMapRows = () => {
  const { t } = useTranslation('followUp');

  return useCallback(
    (data: GaugeData) => {
      const total = data.promoters + data.passives + data.detractors;

      return [
        [
          {
            value: t('npsScore'),
            fontWeight: 'bold',
          },
          { value: data.totalScore },
        ],
        [
          {
            value: t('npsGroup.promoter'),
            fontWeight: 'bold',
          },
          { value: data.promoters },
          {
            value: `${getRelativeValue(data.promoters, total, 0)}%`,
            align: 'right',
          },
        ],
        [
          {
            value: t('npsGroup.passive'),
            fontWeight: 'bold',
          },
          { value: data.passives },
          {
            value: `${getRelativeValue(data.passives, total, 0)}%`,
            align: 'right',
          },
        ],
        [
          {
            value: t('npsGroup.detractor'),
            fontWeight: 'bold',
          },
          { value: data.detractors },
          {
            value: `${getRelativeValue(data.detractors, total, 0)}%`,
            align: 'right',
          },
        ],
      ] satisfies Row[];
    },
    [t],
  );
};

export const useExportOptions = ({
  data,
  comparisonData,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const mapRows = useMapRows();
  const rows = useMemo(() => mapRows(data), [data, mapRows]);
  const comparisonRows = useMemo(
    () => (comparisonData ? mapRows(comparisonData) : undefined),
    [comparisonData, mapRows],
  );

  return useMemo(
    () => ({
      widgetName,
      rows,
      comparisonRows,
      queryFilters,
    }),
    [widgetName, rows, comparisonRows, queryFilters],
  );
};
